import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  UserPlus, Pencil, Trash2, ChevronDown, ChevronRight, Car, Plus, Phone, Mail, MapPin, History, Link2, FileText,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_C = { nom: "", telephone: "", email: "", adresse: "" };
const EMPTY_V = { marque: "", modele: "", immatriculation: "", annee: "", vin: "", kilometrage: 0 };

const STATUTS = {
  diagnostic: { label: "En diagnostic", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30", dot: "#06B6D4" },
  commande_piece: { label: "En commande pièces", color: "bg-amber-500/15 text-amber-400 border-amber-500/30", dot: "#F59E0B" },
  piece_recue: { label: "Pièce reçue", color: "bg-violet-500/15 text-violet-400 border-violet-500/30", dot: "#8B5CF6" },
  reparation: { label: "En réparation", color: "bg-blue-500/15 text-blue-400 border-blue-500/30", dot: "#3B82F6" },
  termine: { label: "Terminé / Prêt", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", dot: "#10B981" },
  annule: { label: "Annulé", color: "bg-red-500/15 text-red-400 border-red-500/30", dot: "#EF4444" },
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [cOpen, setCOpen] = useState(false);
  const [cForm, setCForm] = useState(EMPTY_C);
  const [cEditId, setCEditId] = useState(null);
  const [vOpen, setVOpen] = useState(false);
  const [vForm, setVForm] = useState(EMPTY_V);
  const [vClientId, setVClientId] = useState(null);

  const load = async () => {
    const [c, v, r, ar] = await Promise.all([
      api.get("/clients"), api.get("/vehicles"), api.get("/repairs"),
      api.get("/repairs", { params: { archived: true } }),
    ]);
    setClients(c.data); setVehicles(v.data); setRepairs([...r.data, ...ar.data]);
  };
  useEffect(() => { load(); }, []);

  const saveClient = async () => {
    try {
      if (cEditId) await api.put(`/clients/${cEditId}`, cForm);
      else await api.post("/clients", cForm);
      toast.success("Client enregistré"); setCOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delClient = async (id) => {
    if (!window.confirm("Supprimer ce client et ses véhicules ?")) return;
    await api.delete(`/clients/${id}`); toast.success("Client supprimé"); load();
  };

  const saveVehicle = async () => {
    try {
      await api.post("/vehicles", { ...vForm, client_id: vClientId, kilometrage: Number(vForm.kilometrage) });
      toast.success("Véhicule ajouté"); setVOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delVehicle = async (id) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    await api.delete(`/vehicles/${id}`); load();
  };

  const assignClient = async (veh, clientId) => {
    try {
      await api.put(`/vehicles/${veh.id}`, {
        client_id: clientId, marque: veh.marque, modele: veh.modele,
        immatriculation: veh.immatriculation, annee: veh.annee, vin: veh.vin, kilometrage: veh.kilometrage,
      });
      toast.success("Véhicule rattaché au client"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const unattached = vehicles.filter((v) => !v.client_id);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC]">Clients & Véhicules</h1>
          <p className="text-[#94A3B8] text-sm">Fiches clients et véhicules associés.</p>
        </div>
        <Button data-testid="add-client-button" onClick={() => { setCForm(EMPTY_C); setCEditId(null); setCOpen(true); }} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
          <UserPlus className="w-4 h-4 mr-2" /> Nouveau client
        </Button>
      </div>

      {unattached.length > 0 && (
        <div data-testid="unattached-section" className="bg-[#182232] border border-amber-500/30 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#2A364F] flex items-center gap-2">
            <Link2 className="w-4 h-4 text-amber-400" />
            <h3 className="font-heading font-semibold text-[#F8FAFC]">Véhicules non rattachés</h3>
            <span className="text-xs bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">{unattached.length}</span>
          </div>
          <div className="divide-y divide-[#2A364F]">
            {unattached.map((v) => (
              <div key={v.id} data-testid={`unattached-${v.id}`} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-2 py-0.5 rounded border border-yellow-500/30 uppercase text-xs">{v.immatriculation}</span>
                  <span className="text-[#F8FAFC]">{v.marque} {v.modele}</span>
                  <span className="text-xs text-[#64748B]">{v.kilometrage?.toLocaleString("fr-FR")} km</span>
                </div>
                <div className="flex items-center gap-2">
                  <Select onValueChange={(cid) => assignClient(v, cid)}>
                    <SelectTrigger data-testid={`assign-select-${v.id}`} className="w-56 bg-[#0A0D14] border-[#2A364F] text-sm"><SelectValue placeholder="Rattacher à un client…" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => delVehicle(v.id)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {clients.map((c) => {
          const cars = vehicles.filter((v) => v.client_id === c.id);
          const isOpen = expanded === c.id;
          return (
            <div key={c.id} data-testid={`client-card-${c.id}`} className="bg-[#182232] border border-[#2A364F] rounded-xl overflow-hidden">
              <div className="px-5 py-4 flex items-center justify-between">
                <button onClick={() => setExpanded(isOpen ? null : c.id)} className="flex items-center gap-3 text-left flex-1">
                  {isOpen ? <ChevronDown className="w-5 h-5 text-[#64748B]" /> : <ChevronRight className="w-5 h-5 text-[#64748B]" />}
                  <div>
                    <div className="font-heading font-semibold text-[#F8FAFC]">{c.nom}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#94A3B8] mt-1">
                      {c.telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.telephone}</span>}
                      {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                      <span className="flex items-center gap-1 text-cyan-400"><Car className="w-3 h-3" />{cars.length} véhicule(s)</span>
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-1">
                  <button data-testid={`add-vehicle-${c.id}`} onClick={() => { setVForm(EMPTY_V); setVClientId(c.id); setVOpen(true); }} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-cyan-400 flex items-center justify-center" title="Ajouter véhicule"><Plus className="w-4 h-4" /></button>
                  <button data-testid={`edit-client-${c.id}`} onClick={() => { setCForm({ nom: c.nom, telephone: c.telephone, email: c.email, adresse: c.adresse }); setCEditId(c.id); setCOpen(true); }} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#3B82F6] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-client-${c.id}`} onClick={() => delClient(c.id)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-[#2A364F] p-5 space-y-3">
                  {c.adresse && <div className="text-sm text-[#94A3B8] flex items-center gap-2"><MapPin className="w-4 h-4" />{c.adresse}</div>}
                  {cars.length === 0 ? (
                    <div className="text-sm text-[#64748B]">Aucun véhicule enregistré.</div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {cars.map((v) => {
                        const hist = repairs
                          .filter((r) => r.vehicle_id === v.id)
                          .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                        return (
                        <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="bg-[#0A0D14] border border-[#2A364F] rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-[#F8FAFC] font-medium">{v.marque} {v.modele}</div>
                              <div className="mt-1"><span className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-2 py-0.5 rounded border border-yellow-500/30 uppercase text-xs">{v.immatriculation}</span></div>
                              <div className="text-xs text-[#64748B] mt-1">{v.annee && `${v.annee} · `}{v.kilometrage?.toLocaleString("fr-FR")} km {v.vin && `· VIN ${v.vin}`}</div>
                            </div>
                            <button onClick={() => delVehicle(v.id)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <div className="mt-3 pt-3 border-t border-[#2A364F]">
                            <div className="text-[10px] uppercase tracking-wider text-[#64748B] flex items-center gap-1.5 mb-2">
                              <History className="w-3 h-3" /> Historique réparations
                              <span className="text-cyan-400">({hist.length})</span>
                            </div>
                            {hist.length === 0 ? (
                              <div className="text-xs text-[#64748B]">Aucune réparation enregistrée.</div>
                            ) : (
                              <div className="relative pl-4 space-y-3">
                                <div className="absolute left-[5px] top-1 bottom-1 w-px bg-[#2A364F]" />
                                {hist.map((r) => {
                                  const st = STATUTS[r.statut] || STATUTS.diagnostic;
                                  const tot = (r.main_oeuvre || []).reduce((a, m) => a + m.heures * m.taux, 0) +
                                    (r.pieces_utilisees || []).reduce((a, p) => a + p.quantite * p.prix, 0);
                                  return (
                                    <div key={r.id} data-testid={`vehicle-history-${r.id}`} className="relative">
                                      <div className="absolute -left-4 top-1 w-[11px] h-[11px] rounded-full border-2 border-[#0A0D14]" style={{ backgroundColor: st.dot }} />
                                      <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-xs text-[#94A3B8] font-mono">{(r.created_at || "").slice(0, 10)}</span>
                                        <span className={`text-[10px] px-2 py-0.5 rounded border ${st.color}`}>{st.label}</span>
                                      </div>
                                      <div className="text-sm text-[#F8FAFC] mt-0.5">{r.diagnostic || "Intervention"}</div>
                                      {r.devis_numero && <div data-testid={`vehicle-history-from-devis-${r.id}`} className="text-[10px] text-blue-400 mt-0.5 flex items-center gap-1"><FileText className="w-3 h-3" />Issu du devis {r.devis_numero}</div>}
                                      <div className="text-xs text-[#64748B] mt-0.5">
                                        {r.kilometrage?.toLocaleString("fr-FR")} km · Mécano : {r.mecanicien || "—"} · <span className="font-mono text-[#F59E0B]">{tot.toFixed(2)} €</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {clients.length === 0 && <div className="text-center text-[#64748B] py-10">Aucun client. Créez votre premier client.</div>}
      </div>

      {/* Client dialog */}
      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-heading">{cEditId ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[#94A3B8]">Nom *</Label><Input data-testid="client-nom-input" value={cForm.nom} onChange={(e) => setCForm({ ...cForm, nom: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#94A3B8]">Téléphone</Label><Input value={cForm.telephone} onChange={(e) => setCForm({ ...cForm, telephone: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
              <div><Label className="text-[#94A3B8]">Email</Label><Input value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            </div>
            <div><Label className="text-[#94A3B8]">Adresse</Label><Input value={cForm.adresse} onChange={(e) => setCForm({ ...cForm, adresse: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-client-button" onClick={saveClient} disabled={!cForm.nom} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle dialog */}
      <Dialog open={vOpen} onOpenChange={setVOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-heading">Nouveau véhicule</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[#94A3B8]">Marque *</Label><Input data-testid="vehicle-marque-input" value={vForm.marque} onChange={(e) => setVForm({ ...vForm, marque: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Modèle</Label><Input value={vForm.modele} onChange={(e) => setVForm({ ...vForm, modele: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div className="col-span-2"><Label className="text-[#94A3B8]">Immatriculation *</Label><Input data-testid="vehicle-immat-input" value={vForm.immatriculation} onChange={(e) => setVForm({ ...vForm, immatriculation: e.target.value.toUpperCase() })} className="mt-1 bg-[#182232] border-[#2A364F] font-mono" placeholder="AA-123-BB" /></div>
            <div><Label className="text-[#94A3B8]">Année</Label><Input value={vForm.annee} onChange={(e) => setVForm({ ...vForm, annee: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Kilométrage</Label><Input type="number" value={vForm.kilometrage} onChange={(e) => setVForm({ ...vForm, kilometrage: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div className="col-span-2"><Label className="text-[#94A3B8]">VIN</Label><Input value={vForm.vin} onChange={(e) => setVForm({ ...vForm, vin: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F] font-mono" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-vehicle-button" onClick={saveVehicle} disabled={!vForm.marque || !vForm.immatriculation} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
